import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircleAlert, PackageOpen } from 'lucide-react';
import EmptyState from '../../components/site/EmptyState';
import { productsAPI } from '../../api/apiService';
import { getErrorMessage } from '../../utils/apiError';
import { useAppDispatch } from '../../state/AppStateProvider';
import { PRODUCT_TYPE } from '../allProducts/state/Types';
import ProductGallery from './widgets/productGallery';
import ProductBuyCard from './widgets/productDetailsSide';
import ProductDescriptionSection from './widgets/productDescriptionSection';
import ProductReviewSection, { useProductReviews } from './widgets/productReview';
import RelatedProjects from './widgets/relatedProjects';
import './style.css';

const SITE_NAME = 'Projects Kart';

// Sets the tab title while the page shows a project and puts the old one back afterwards.
const useDocumentTitle = title => {
  useEffect(() => {
    if (!title) return undefined;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
};

const Crumbs = ({ product }) => (
  <nav aria-label="Breadcrumb">
    <ol className="pk-crumbs">
      <li>
        <Link to="/">Home</Link>
      </li>
      <li>
        {product ? <Link to="/products">Projects</Link> : <span aria-current="page">Projects</span>}
      </li>
      {product?.category && (
        <li>
          <Link to={`/products?category=${encodeURIComponent(product.category)}`}>
            {product.category}
          </Link>
        </li>
      )}
      {product && (
        <li>
          <span aria-current="page">{product.name}</span>
        </li>
      )}
    </ol>
  </nav>
);

const DetailsSkeleton = () => (
  <div aria-hidden="true">
    <span className="pk-skel pd-skel-line pd-skel-crumbs" />
    <div className="pk-split pd-split">
      <div className="pd-split__gallery">
        <div className="pd-gallery__frame pk-skel" />
      </div>
      <div className="pd-split__buy">
        <div className="pk-card pd-buy">
          <span className="pk-skel pd-skel-line pd-skel-line--short" />
          <span className="pk-skel pd-skel-title" />
          <span className="pk-skel pd-skel-line" />
          <span className="pk-skel pd-skel-line" />
          <span className="pk-skel pd-skel-price" />
          <span className="pk-skel pd-skel-button" />
        </div>
      </div>
    </div>
  </div>
);

const ProductDetails = () => {
  const { productId } = useParams();
  const dispatch = useAppDispatch();
  const [state, setState] = useState({ status: 'loading', product: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  // Reviews load alongside the product.
  const reviews = useProductReviews(productId);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', product: null, error: null });

    productsAPI
      .getById(productId)
      .then(response => {
        if (!active) return;
        const product = response.data?.data;
        if (!product?.id) {
          setState({ status: 'notfound', product: null, error: null });
          return;
        }
        setState({ status: 'ready', product, error: null });
        // Checkout reads the product from the store, so it can skip a request.
        dispatch({ type: PRODUCT_TYPE.PRODUCT_DETAILS, payload: product });
      })
      .catch(error => {
        if (!active) return;
        if (error.response?.status === 404) {
          setState({ status: 'notfound', product: null, error: null });
        } else {
          setState({
            status: 'error',
            product: null,
            error: getErrorMessage(error, "We couldn't load this project. Please try again."),
          });
        }
      });

    return () => {
      active = false;
    };
  }, [productId, reloadKey, dispatch]);

  const { status, product, error } = state;

  useDocumentTitle(
    status === 'ready'
      ? `${product.name || 'Project'} | ${SITE_NAME}`
      : status === 'notfound'
        ? `Project not found | ${SITE_NAME}`
        : null
  );

  if (status === 'loading') {
    return (
      <main className="pk-page pd-page" aria-busy="true">
        <div className="pk-container">
          <p className="sr-only" role="status">
            Loading project
          </p>
          <DetailsSkeleton />
        </div>
      </main>
    );
  }

  if (status === 'notfound' || status === 'error') {
    const notFound = status === 'notfound';
    return (
      <main className="pk-page pd-page">
        <div className="pk-container">
          <Crumbs />
          <EmptyState
            role={notFound ? undefined : 'alert'}
            icon={notFound ? <PackageOpen /> : <CircleAlert />}
            title={notFound ? 'Project not found' : "Couldn't load this project"}
            text={
              notFound
                ? "This project doesn't exist or is no longer available."
                : error
            }
            actions={
              <>
                {!notFound && (
                  <button
                    type="button"
                    className="pk-btn pk-btn--primary pk-btn--sm"
                    onClick={() => setReloadKey(k => k + 1)}
                  >
                    Retry
                  </button>
                )}
                <Link
                  className={`pk-btn pk-btn--sm ${notFound ? 'pk-btn--primary' : 'pk-btn--secondary'}`}
                  to="/products"
                >
                  Browse projects
                </Link>
              </>
            }
          />
        </div>
      </main>
    );
  }

  // The long description is the "About" text; the short one sits in the buy card unless it's the same text.
  const shortDescription = (product.sortDescription || '').trim();
  const about = (product.longDescription || '').trim() || shortDescription;
  const cardDescription = shortDescription && shortDescription !== about ? shortDescription : null;

  return (
    <main className="pk-page pd-page">
      <div className="pk-container">
        <Crumbs product={product} />

        <div className="pk-split pd-split">
          <div className="pd-split__gallery">
            <ProductGallery key={product.id} product={product} />
          </div>

          <aside className="pd-split__buy" aria-label="Buy this project">
            <ProductBuyCard product={product} description={cardDescription} reviews={reviews} />
          </aside>

          <div className="pd-split__content">
            <ProductDescriptionSection product={product} about={about} />
            <ProductReviewSection productId={product.id} reviews={reviews} />
          </div>
        </div>

        <RelatedProjects productId={product.id} category={product.category} />
      </div>
    </main>
  );
};

export default ProductDetails;
